import { createContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { AuthContextType, UserProfile, ProfileCompletionStatus } from './types';
import { createAuthServices } from './services';

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isProfileLoading, setIsProfileLoading] = useState<boolean>(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Enhanced debug logging for auth flow
  if (process.env.NODE_ENV === 'development') {
    console.log("AuthProvider render:", { 
      hasSession: !!session, 
      hasUser: !!user, 
      hasProfile: !!userProfile, 
      isLoading,
      isProfileLoading,
      userRole: userProfile?.role,
      isAdmin: userProfile?.role === 'admin',
      userId: user?.id,
      timestamp: new Date().toISOString()
    });
  }

  // Create auth services with dependencies
  const authServices = createAuthServices(navigate, toast, setUserProfile);

  useEffect(() => {
    let isMounted = true;
    let initialized = false;
    
    // Unified function to handle auth state updates with enhanced error handling
    const handleAuthState = async (session: Session | null, skipInitializedCheck = false) => {
      if (!isMounted) return;
      
      console.log("🔄 Auth state change:", { 
        sessionExists: !!session, 
        userId: session?.user?.id,
        skipInitializedCheck,
        timestamp: new Date().toISOString()
      });
      
      // Set session and user state synchronously
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        // Start profile loading with better error handling
        setIsProfileLoading(true);
        console.log("📥 Starting profile fetch for user:", session.user.id);
        
        try {
          await authServices.fetchUserProfile(session.user.id);
          console.log("✅ Profile fetch completed successfully");
        } catch (error) {
          console.error('❌ Failed to fetch user profile:', error);
          // Set a retry mechanism for profile loading
          setTimeout(() => {
            if (isMounted && session?.user) {
              console.log("🔄 Retrying profile fetch...");
              authServices.fetchUserProfile(session.user.id).catch(console.error);
            }
          }, 2000);
        } finally {
          if (isMounted) {
            setIsProfileLoading(false);
            console.log("🏁 Profile loading state cleared");
          }
        }
      } else {
        console.log("🧹 Clearing user profile (no session)");
        setUserProfile(null);
        setIsProfileLoading(false);
      }
      
      // Set loading to false after profile attempt
      if (!initialized || skipInitializedCheck) {
        initialized = true;
        if (isMounted) {
          setIsLoading(false);
          console.log("🚀 Auth initialization complete");
        }
      }
    };
    
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Only handle auth changes after initial load is complete
        if (initialized) {
          handleAuthState(session, true);
        }
      }
    );

    // Initialize auth state - this should be fast
    const initializeAuth = async () => {
      try {
        if (!isMounted) return;
        
        const { data: { session } } = await supabase.auth.getSession();
        handleAuthState(session);
      } catch (error) {
        console.error("Error initializing auth:", error);
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Wrapper functions that provide the current user context
  const signIn = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      await authServices.signIn(email, password);
    } finally {
      setIsLoading(false);
    }
  };

  const updateThemePreference = async (theme: string) => {
    if (!user) return;
    await authServices.updateThemePreference(theme, user.id);
  };

  // Manual profile refresh function
  const refreshProfile = async () => {
    if (!user) {
      console.log("⚠️ Cannot refresh profile: no user");
      return false;
    }
    
    console.log("🔄 Manual profile refresh requested for user:", user.id);
    setIsProfileLoading(true);
    
    try {
      await authServices.fetchUserProfile(user.id);
      console.log("✅ Manual profile refresh successful");
      return true;
    } catch (error) {
      console.error('❌ Manual profile refresh failed:', error);
      return false;
    } finally {
      setIsProfileLoading(false);
    }
  };

  // Update profile status function
  const updateProfileStatus = async (status: ProfileCompletionStatus) => {
    if (!user) {
      throw new Error("User must be authenticated to update profile status");
    }
    
    try {
      const { error } = await supabase.rpc('update_profile_status', {
        user_id: user.id,
        new_status: status
      });
      
      if (error) throw error;
      
      // Refresh profile to get updated status
      await refreshProfile();
    } catch (error) {
      console.error('Failed to update profile status:', error);
      throw error;
    }
  };

  const isAdmin = userProfile?.role === 'admin';
  const isAuthenticated = !!user;
  const isAuthReady = isAuthenticated ? !isProfileLoading : !isLoading;
  const profileStatus = userProfile?.profile_completion_status || null;
  
  // Enhanced logging for admin status
  if (process.env.NODE_ENV === 'development' && isAuthenticated) {
    console.log("👤 Auth status check:", {
      isAuthenticated,
      isAdmin,
      isAuthReady,
      userRole: userProfile?.role,
      hasProfile: !!userProfile,
      isProfileLoading,
      userId: user?.id
    });
  }

  const value = {
    session,
    user,
    userProfile,
    isLoading,
    isProfileLoading,
    isAdmin,
    isAuthenticated,
    isAuthReady,
    profileStatus,
    signIn,
    signOut: authServices.signOut,
    forgotPassword: authServices.forgotPassword,
    updateThemePreference,
    refreshProfile,
    updateProfileStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}